import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CheckCheck, Download, Loader2, Square, X } from 'lucide-react';
import './assetExport.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api/asset-export/preferences';

export const AssetExportDialog = ({ onClose, onExport }) => {
  const ref = useRef(null);
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(API, { withCredentials: true, signal });
      setColumns(data.columns);
      setSelected(data.selected);
    } catch (e) {
      if (!axios.isCancel(e)) setError('No se pudo cargar tu selección de columnas.');
    } finally { if (!signal?.aborted) setLoading(false); }
  }, []);
  useEffect(() => {
    const dialog = ref.current;
    const controller = new AbortController();
    dialog.showModal();
    load(controller.signal);
    return () => { controller.abort(); dialog.close(); };
  }, [load]);
  const submit = async () => {
    if (!selected.length || saving) return;
    setSaving(true);
    setError('');
    try {
      // Guardar sólo la decisión confirmada, nunca los cambios de un panel cancelado.
      await axios.put(API, { columns: selected }, { withCredentials: true });
      if (await onExport(selected)) onClose();
      else setError('No se pudo generar el archivo. Tu selección está guardada; puedes reintentar.');
    } catch { setError('No se pudo guardar tu selección. No se ha generado ningún archivo.'); }
    finally { setSaving(false); }
  };
  const toggle = key => setSelected(previous => previous.includes(key) ? previous.filter(k => k !== key) : [...previous, key]);

  return <dialog ref={ref} className="asset-export-dialog" data-testid="asset-export-dialog" aria-labelledby="asset-export-title" onCancel={e => { e.preventDefault(); if (!saving) onClose(); }}>
    <header className="flex items-start justify-between gap-3">
      <div><h2 id="asset-export-title" className="text-lg font-semibold" data-testid="asset-export-title">Columnas del CSV</h2><p className="text-sm mt-1 opacity-70" data-testid="asset-export-count">{loading ? 'Cargando selección…' : `${selected.length} de ${columns.length} columnas seleccionadas`}</p></div>
      <button type="button" onClick={onClose} disabled={saving} className="p-2 rounded hover:bg-black/5" aria-label="Cerrar selección de columnas" title="Cerrar" data-testid="asset-export-close"><X className="w-5 h-5" /></button>
    </header>
    {error && <p role="alert" className="text-sm text-red-600 mt-3" data-testid="asset-export-error">{error}</p>}
    {loading ? <div className="flex justify-center py-12" data-testid="asset-export-loading"><Loader2 className="w-6 h-6 animate-spin" /></div> : columns.length ? <>
      <div className="flex flex-wrap gap-3 py-4">
        <button type="button" className="btn-secondary text-xs inline-flex gap-2 items-center" disabled={saving} onClick={() => setSelected(columns.map(c => c.key))} data-testid="asset-export-select-all"><CheckCheck className="w-4 h-4" />Marcar todas</button>
        <button type="button" className="btn-secondary text-xs inline-flex gap-2 items-center" disabled={saving} onClick={() => setSelected([])} data-testid="asset-export-select-none"><Square className="w-4 h-4" />Desmarcar todas</button>
      </div>
      <div className="asset-export-column-grid" data-testid="asset-export-columns">
        {columns.map(c => <label key={c.key} className="asset-export-column"><input type="checkbox" className="w-4 h-4 shrink-0 accent-cyan-600" checked={selected.includes(c.key)} disabled={saving} onChange={() => toggle(c.key)} data-testid={`asset-export-column-${c.key}`} /><span>{c.label}</span></label>)}
      </div>
      {!selected.length && <p className="text-sm mt-3 text-amber-600" data-testid="asset-export-empty-warning">Selecciona al menos una columna.</p>}
    </> : <button type="button" className="btn-secondary mt-4" onClick={() => load()} data-testid="asset-export-retry">Reintentar</button>}
    <footer className="flex flex-wrap justify-end gap-3 pt-5">
      <button type="button" className="btn-secondary" disabled={saving} onClick={onClose} data-testid="asset-export-cancel">Cancelar</button>
      <button type="button" className="btn-primary inline-flex items-center gap-2" disabled={loading || saving || !selected.length} onClick={submit} data-testid="asset-export-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{saving ? 'Exportando…' : 'Exportar CSV'}</button>
    </footer>
  </dialog>;
};